import { useEffect, useState } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ArticleCard from "@/components/ArticleCard";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, LineChart, Sprout, Calculator, Leaf, Zap, Gauge, ClipboardCheck, FileCheck2 } from "lucide-react";
import { applySeo } from "@/lib/seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const IMG = {
  hero: `${BACKEND_URL}/api/images/hero-wind.jpg`,
  solar: `${BACKEND_URL}/api/images/home-solar.jpg`,
  arch: `${BACKEND_URL}/api/images/home-architecture.jpg`,
  editorial: `${BACKEND_URL}/api/images/home-editorial.jpg`,
};

export default function Home() {
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    api.get("/articles").then((r) => setArticles(r.data)).catch(() => {});
    applySeo({
      title: "Grüne Geldanlage – Nachhaltige Geldanlagen fundiert erklärt",
      description: "Unabhängiges Fachportal für nachhaltige Geldanlagen: ESG-Fonds, grüne ETFs, Impact Investing, EU-Taxonomie, SFDR. Faktenbasiert, ohne Greenwashing.",
      canonicalPath: "/",
    });
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", "Grüne Geldanlage – Nachhaltige Geldanlagen fundiert erklärt");
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) ogImage.setAttribute("content", IMG.hero);
    const twImage = document.querySelector('meta[name="twitter:image"]');
    if (twImage) twImage.setAttribute("content", IMG.hero);
  }, []);

  const featured = articles[0];
  const rest = articles.slice(1, 3);

  return (
    <div className="min-h-screen bg-brand-paper">
      <Header />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24" data-testid="home-hero">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 animate-fade-up">
            <div className="overline mb-5">Editorial · Ausgabe {new Date().getFullYear()}</div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight text-brand-dark mb-8">
              Wirkung <em className="italic text-brand-primary">und</em> Wachstum.<br />
              Für Anleger, die beides ernst nehmen.
            </h1>
            <p className="text-lg text-neutral-700 max-w-xl mb-8 leading-relaxed">
              Grüne Geldanlage ist das unabhängige Fachportal für nachhaltige Geldanlagen im
              deutschsprachigen Raum: ESG-Fonds, grüne ETFs, Impact Investing – erklärt mit
              journalistischer Sorgfalt und ohne Greenwashing-Kosmetik.
            </p>
            {featured && (
              <Link to={`/artikel/${featured.slug}`} className="inline-flex items-center gap-2 text-brand-primary font-medium no-underline hover:text-brand-hover" data-testid="hero-cta">
                Hauptartikel lesen <ArrowUpRight size={18} strokeWidth={1.5} />
              </Link>
            )}
          </div>
          <div className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden bg-neutral-100">
              <img
                src={IMG.hero}
                alt="Weiße Windkraftanlagen auf grünen Hügeln im Abendlicht – Symbolbild für nachhaltige Geldanlagen"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* EDITORIAL with inline unique images */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16" data-testid="home-editorial">
        <div className="overline mb-4">Über dieses Portal</div>
        <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight mb-8">
          Nachhaltig investieren – ohne Wischiwaschi
        </h2>
        <div className="prose-editorial max-w-none">
          <p>
            Der Kapitalmarkt ist in Bewegung: Immer mehr Anleger möchten ihr Geld nicht nur nach
            klassischen Rendite-Kriterien anlegen, sondern auch nach ökologischen und sozialen
            Maßstäben. Grüne Geldanlage begleitet diesen Wandel mit einer klaren redaktionellen
            Haltung – als deutschsprachiges Journal für Privatanleger, die verstehen möchten,
            wie nachhaltige Geldanlagen wirklich funktionieren, wo ihre Chancen liegen und
            welche Grenzen sie haben.
          </p>

          <figure className="my-10 md:float-right md:ml-8 md:w-2/5">
            <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
              <img src={IMG.solar} alt="Großflächige Solaranlage mit dunklen Photovoltaikmodulen in Reihenanordnung unter klarem Himmel" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <figcaption>Erneuerbare Energien als Kernbaustein vieler nachhaltiger Anlagestrategien.</figcaption>
          </figure>

          <h3>Worum es auf diesem Portal geht</h3>
          <p>
            Nachhaltige Geldanlagen sind längst kein Nischenthema mehr. ESG-Fonds, grüne ETFs,
            Impact-Investments, Green Bonds und nachhaltige Einzelaktien haben sich zu einer
            ernsthaften Anlageklasse entwickelt. Gleichzeitig ist der Markt unübersichtlich
            geworden: Ratingagenturen kommen zu unterschiedlichen Urteilen, Fondsanbieter
            überbieten sich mit grünen Versprechen, und der regulatorische Rahmen – von der
            EU-Taxonomie bis zur SFDR-Offenlegungsverordnung – entwickelt sich rasant weiter.
            Grüne Geldanlage sortiert dieses Feld: Wir erklären ESG-Kriterien in verständlicher
            Sprache, vergleichen konkrete Produkte, zeigen typische Fallstricke – und stellen
            Instrumente bereit, mit denen Sie eigene Berechnungen anstellen können.
          </p>

          <h3>Für wen dieses Portal gemacht ist</h3>
          <p>
            Unsere Leser sind Privatanleger im deutschsprachigen Raum – Einsteiger, die sich zum
            ersten Mal mit nachhaltigen Geldanlagen beschäftigen, ebenso wie fortgeschrittene
            Investoren, die ihr Portfolio gezielt um Impact-Bausteine ergänzen wollen. Sie eint
            der Wunsch, Rendite nicht länger als isolierten Wert zu betrachten, sondern in
            einem breiteren Kontext: Welche Wirkung erzielt mein Kapital? Welche Risiken
            entstehen aus Klimawandel, Regulierung und gesellschaftlichem Wertewandel für mein
            Depot? Und wie lassen sich diese Fragen mit klassischen Kriterien wie Kosten,
            Diversifikation und langfristigem Vermögensaufbau in Einklang bringen?
          </p>

          <figure className="my-10 md:float-left md:mr-8 md:w-2/5">
            <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
              <img src={IMG.arch} alt="Modernes nachhaltiges Bürogebäude mit begrünter Fassade und Holz-Glas-Konstruktion" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <figcaption>Nachhaltiges Bauen und ressourcenschonende Infrastruktur.</figcaption>
          </figure>

          <h3>Unser redaktioneller Anspruch</h3>
          <p>
            Grüne Geldanlage arbeitet unabhängig. Wir verkaufen keine Fonds, wir vergeben keine
            Ratings und wir schreiben keine Anlageberatung. Was wir tun: Wir recherchieren
            faktenbasiert, prüfen Quellen mehrfach und benennen Interessenkonflikte offen. Wenn
            ein Artikel Affiliate-Links enthält, weisen wir das transparent aus. Wenn eine
            Studie methodische Schwächen hat, ordnen wir das ein. Wir bewegen uns damit
            bewusst in der Tradition des journalistischen Fachmediums – nicht des
            Marketing-Blogs. Zu unserer Haltung gehört auch, ESG-Ratings kritisch zu begleiten:
            Wir zeigen, warum verschiedene Anbieter zu unterschiedlichen Urteilen kommen,
            welche Grenzen die aktuelle Methodik hat und wie Greenwashing im
            Anlagemarkt konkret aussieht.
          </p>

          <h3>Was Sie auf diesem Portal konkret vorfinden</h3>
          <p>
            Im redaktionellen Teil erwarten Sie ausführliche Artikel zu den zentralen Themen
            nachhaltigen Investierens: Grundlagen der ESG-Bewertung, Vergleiche zwischen
            passiven und aktiven Produkten, praxisnahe Prüfanleitungen gegen Greenwashing sowie
            Analysen zu regulatorischen Entwicklungen. Ergänzend finden Sie hier sechs
            interaktive Rechner: Der <strong>Nachhaltigkeits-Rendite-Rechner</strong>{" "}
            zeigt, wie sich ein ESG-ETF-Sparplan über die Jahre entwickeln kann. Der{" "}
            <strong>CO₂-Fußabdruck-Portfolio-Rechner</strong>{" "}
            hilft Ihnen einzuschätzen, wie stark sich die Emissionsintensität Ihres Depots
            verändert. Der <strong>Green Premium Rechner</strong>{" "}
            beziffert die tatsächlichen Opportunitätskosten – also wie viel Rendite ein
            Wechsel von klassisch zu nachhaltig langfristig „kostet". Der{" "}
            <strong>Grüne Hebel-Simulator</strong> übersetzt die CO₂-Wirkung Ihrer Anlage in
            greifbare Alltagsvergleiche – von veganer Ernährung bis Autoverzicht. Das{" "}
            <strong>ESG-Fit-Quiz</strong> führt Sie in fünf Fragen zu einem nachhaltigen ETF,
            der zu Ihrem Anlageprofil passt. Und der{" "}
            <strong>Nachhaltigkeitspräferenz-Assistent</strong> bereitet Sie in rund fünf Minuten
            auf das Beratungsgespräch mit Ihrer Bank vor – inklusive PDF zum Mitnehmen.
          </p>

          <figure className="my-10 md:float-right md:ml-8 md:w-2/5">
            <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
              <img src={IMG.editorial} alt="Redaktionsschreibtisch mit gedruckter Finanzzeitung, Laptop mit gedämpften Charts und Fachbüchern" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <figcaption>Redaktionelle Sorgfalt: Recherche entlang von Primärquellen.</figcaption>
          </figure>

          <h3>Warum das Thema jetzt zählt</h3>
          <p>
            Klimawandel, Ressourcenknappheit und regulatorischer Druck verändern die
            Bewertungsgrundlage vieler Unternehmen fundamental. Die EU-Taxonomie definiert
            erstmals einheitliche Kriterien für ökologische Nachhaltigkeit; die SFDR
            verpflichtet Anbieter zur Offenlegung. Anlegerinteresse und Regulierung treiben die
            Entwicklung gemeinsam voran – und schaffen einen Markt, der sich in wenigen Jahren
            mehr verändern wird als in den zwei Jahrzehnten davor. Wer heute ein
            nachhaltiges Depot aufbaut, tut das in einem regulatorischen und methodischen
            Umfeld, das in Bewegung ist. Grüne Geldanlage begleitet diese Bewegung
            journalistisch – ohne Öko-Pathos, aber mit Substanz.
          </p>
          <div className="clear-both"></div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-white border-y border-neutral-200" data-testid="home-values">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-3 gap-12">
          {[
            { icon: ShieldCheck, title: "ESG-Transparenz", body: "Wir zeigen offen, wie ESG-Ratings zustande kommen – inklusive ihrer Grenzen und methodischen Unterschiede." },
            { icon: Sprout, title: "Langfristige Wirkung", body: "Nachhaltigkeit ist kein Sprint. Wir bewerten Produkte mit dem Horizont echter Impact-Investoren, nicht mit Quartalslogik." },
            { icon: LineChart, title: "Kosten & Rendite im Blick", body: "Grüner Anspruch entbindet nicht von finanzieller Sorgfalt. TER, Steuern und Vorabpauschalen bleiben Teil jeder Analyse." },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title}>
              <Icon strokeWidth={1.5} size={28} className="text-brand-primary mb-4" />
              <h3 className="font-serif text-2xl font-medium mb-2">{title}</h3>
              <p className="text-neutral-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CALCULATORS TEASER — the ONLY link to calculators from home */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="home-calculators-teaser">
        <div className="max-w-2xl mb-14">
          <div className="overline mb-3">Werkzeuge</div>
          <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight mb-4">Sechs Rechner. Kein Marketing.</h2>
          <p className="text-neutral-600 text-lg">Interaktive Werkzeuge für Rendite, Emissionen, Opportunitätskosten, Impact-Wirkung, ETF-Passung und die Vorbereitung Ihres Beratungsgesprächs. Jeder Rechner auf einer eigenen Seite mit ausführlicher Erklärung.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { to: "/rechner/nachhaltigkeits-rendite", icon: Calculator, title: "Nachhaltigkeits-Rendite", body: "ETF-Sparplan über X Jahre – Endkapital, Wertzuwachs und Renditepfad." },
            { to: "/rechner/co2-fussabdruck", icon: Leaf, title: "CO₂-Fußabdruck", body: "Emissionsintensität Ihres Depots je Portfolio-Mix – klassisch bis Impact." },
            { to: "/rechner/green-premium", icon: Zap, title: "Green Premium", body: "Opportunitätskosten klassisch vs. nachhaltig – die Kosten des guten Gewissens." },
            { to: "/rechner/gruener-hebel", icon: Gauge, title: "Grüner Hebel", body: "Impact Ihrer Anlage in Alltagsvergleichen – vegan, Autoverzicht, Bäume." },
            { to: "/rechner/nachhaltiger-etf-finder", icon: ClipboardCheck, title: "ESG-Fit-Quiz", body: "Welcher nachhaltige ETF passt zu Ihnen? In 5 Fragen zur persönlichen Empfehlung." },
            { to: "/rechner/nachhaltigkeitspraeferenz-assistent", icon: FileCheck2, title: "Nachhaltigkeitspräferenz-Assistent", body: "Beratungsgespräch vorbereiten – mit PDF-Ausdruck der eigenen Präferenz." },
          ].map(({ to, icon: Icon, title, body }) => (
            <div key={to} className="border border-neutral-200 bg-white p-8 flex flex-col" data-testid={`rechner-teaser-${to.split("/").pop()}`}>
              <Icon strokeWidth={1.5} size={26} className="text-brand-primary mb-5" />
              <h3 className="font-serif text-2xl font-medium mb-3 text-brand-dark">{title}</h3>
              <p className="text-neutral-600 leading-relaxed mb-6 flex-1">{body}</p>
              <Link
                to={to}
                data-testid={`rechner-cta-${to.split("/").pop()}`}
                className="inline-flex items-center justify-center gap-2 bg-brand-primary text-white px-5 py-3 text-sm font-medium tracking-wide no-underline hover:bg-brand-hover transition-colors self-start"
              >
                Zum Rechner <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          ))}
        </div>
        <div className="flex justify-center mt-14">
          <Link
            to="/rechner"
            data-testid="home-rechner-hub-cta"
            className="inline-flex items-center gap-2 border border-brand-primary text-brand-primary px-7 py-3.5 text-sm font-medium tracking-wide no-underline hover:bg-brand-primary hover:text-white transition-colors"
          >
            Alle Rechner &amp; Tools in der Übersicht <ArrowUpRight size={16} strokeWidth={1.5} />
          </Link>
        </div>
      </section>
      <section className="bg-brand-dark text-white" data-testid="home-market-teaser">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-24 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-accent mb-4">Marktdaten Deutschland</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight tracking-tight mb-6">
              Nachhaltige Geldanlagen 2025: <em className="italic text-brand-accent">stabil</em> trotz Gegenwind, weltweit erstmals Abflüsse.
            </h2>
            <p className="text-neutral-300 text-lg max-w-xl mb-6 leading-relaxed">
              Der FNG-Marktbericht 2026 erfasst zum Stichtag 31.12.2025 in Deutschland{" "}
              <strong className="text-white">598,5 Milliarden Euro</strong> in nachhaltigen Publikumsfonds,
              Spezialfonds, Mandaten, Bankanlagen und selbstverwalteten Assets. Weltweit verzeichnen
              nachhaltige Fonds erstmals seit Beginn der Erhebung Nettoabflüsse.
            </p>
            <p className="text-neutral-400 text-sm max-w-xl mb-8 leading-relaxed">
              <strong className="text-neutral-200">Methodik-Hinweis:</strong> Umfrage bei 77 Instituten
              (davon 57 in Deutschland). Wechselnde Teilnehmergruppe – deshalb kein direkter
              Vorjahresvergleich der Volumina.
            </p>
            <Link
              to="/nachhaltige-geldanlage-deutschland"
              data-testid="home-market-cta"
              className="inline-flex items-center gap-2 bg-brand-accent text-brand-dark px-7 py-4 text-sm font-semibold tracking-wide no-underline hover:bg-white transition-colors"
            >
              Zum Marktreport <ArrowUpRight size={16} strokeWidth={2} />
            </Link>
          </div>
          <div className="md:col-span-5 space-y-5">
            <div className="border border-white/15 p-8 md:p-10">
              <div className="text-xs uppercase tracking-[0.2em] text-brand-accent mb-4">Erfasste nachhaltige Geldanlagen 2025</div>
              <div className="font-mono text-5xl md:text-6xl leading-none mb-2">598,5</div>
              <div className="text-neutral-400 mb-2">Milliarden Euro · Deutschland</div>
              <div className="text-xs text-neutral-500">FNG-Stichprobe, Stichtag 31.12.2025</div>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {[
                { top: "62 %", label: "schließen sämtliche Waffen aus", sub: "2024: 72 %" },
                { top: "29 %", label: "der Institute haben ihr ESG-Angebot ausgebaut", sub: "1 % reduziert" },
                { top: "−84 Mrd. USD", label: "Nettoabflüsse nachhaltige Fonds weltweit 2025", sub: "laut Morningstar" },
              ].map((r) => (
                <div key={r.label} className="border border-white/10 bg-white/5 p-5">
                  <div className="font-mono text-2xl text-brand-accent mb-1">{r.top}</div>
                  <div className="text-sm text-neutral-200 leading-snug">{r.label}</div>
                  <div className="text-xs text-neutral-500 mt-1">{r.sub}</div>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-neutral-500 leading-relaxed">
              Quelle: FNG-Marktbericht Nachhaltige Geldanlagen 2026 (Deutschland/Österreich);
              Morningstar laut Vividam/Cash. Stand: Stichtag 31.12.2025, veröffentlicht 17.06.2026.
            </div>
          </div>
        </div>
      </section>

      {/* ARTICLES PREVIEW */}
      <section className="bg-white border-t border-neutral-200" data-testid="home-articles">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="mb-12">
            <div className="overline mb-3">Aus der Redaktion</div>
            <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight">Aktuelle Artikel</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-14">
            {featured && <ArticleCard article={featured} featured />}
            {rest.map((a) => (<ArticleCard key={a.id} article={a} />))}
          </div>
          <div className="flex justify-center mt-16 md:mt-20">
            <Link
              to="/blog"
              data-testid="home-articles-cta"
              className="inline-flex items-center gap-2 bg-brand-primary text-white px-8 py-4 text-sm font-medium tracking-wide no-underline hover:bg-brand-hover transition-colors"
            >
              Alle Artikel in der Übersicht <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
